/** @jsx jsx */
import React from 'react';
import { styled, cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import InlineCardCreateTrigger from '@atlassian/jira-platform-inline-card-create/src/ui/trigger/main.tsx';
import type { EpicIssueType } from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	stickyButtonContainer: {
		display: 'flex',
		justifyContent: 'left',
		position: 'sticky',
		bottom: '0',
		paddingTop: token('space.025'),
		paddingRight: 0,
		paddingBottom: token('space.025'),
		paddingLeft: 0,
		backgroundColor: `${token('elevation.surface.sunken')}`,
		borderRadius: '0 0 6px 6px',
		marginLeft: token('space.075'),
	},
	themed: {
		backgroundColor: 'transparent',
	},
});

export type CreateIssueButtonProps = {
	issueType: EpicIssueType;
	hierarchyName?: string | null;
	onCreate: () => void;
};

const CreateIssueButton = ({ onCreate, issueType, hierarchyName }: CreateIssueButtonProps) => {
	const { formatMessage } = useIntl();
	const { isSoftwareFullTheming } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	const label = hierarchyName?.toLocaleLowerCase() || issueType.name.toLocaleLowerCase();

	return isVisualRefreshEnabled() ? (
		<div
			css={[styles.stickyButtonContainer, isSoftwareFullTheming && styles.themed]}
			data-testid="software-backlog-panel.ui.epic.epic-panel.create-issue-button.sticky-button-container"
		>
			<InlineCardCreateTrigger
				label={formatMessage(messages.label, {
					issueTypeNameLowerCase: label,
				})}
				onClick={onCreate}
			/>
		</div>
	) : (
		<StickyButtonContainer data-testid="software-backlog-panel.ui.epic.epic-panel.create-issue-button.sticky-button-container">
			<InlineCardCreateTrigger
				label={formatMessage(messages.label, {
					issueTypeNameLowerCase: label,
				})}
				onClick={onCreate}
			/>
		</StickyButtonContainer>
	);
};

export default CreateIssueButton;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StickyButtonContainer = styled.div({
	display: 'flex',
	justifyContent: 'left',
	position: 'sticky',
	bottom: '0',
	paddingTop: token('space.025'),
	paddingRight: 0,
	paddingBottom: token('space.025'),
	paddingLeft: 0,

	backgroundColor: `${token('elevation.surface.sunken')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `0 0 ${borderRadius * 2}px ${borderRadius * 2}px`,
});
